import { Link } from "@heroui/react";

import { useClaimsContext } from "@/context/claims/useClaimsContext";
import currency from "@/lib/currency";
import { formatClaimableAmount } from "@/lib/currency/tokenAmount";
import { Card } from "@/ui/nextui/Card/Card";

import { ClaimDailyRewards } from "../ClaimDailyRewards/ClaimDailyRewards";

export const DailyRewardsCard: React.FC = () => {
  const { calculateUnlockedRewardsResponse, isRewardsCardLoading } = useClaimsContext();

  const isClaimDataLoading = isRewardsCardLoading || !calculateUnlockedRewardsResponse;

  return (
    <Card
      className="h-full overflow-visible border-1 border-gray-800 bg-background pb-0"
      id="daily-rewards-card"
    >
      <Card.Header className="overflow-hidden p-0" id="daily-rewards-card-header">
        <img
          alt="agent-license-banner"
          className="h-25 w-full object-cover"
          id="daily-rewards-card-banner-image"
          loading="lazy"
          src="/agent-license-flicker.gif"
          style={{
            clipPath: "polygon(0 0, calc(100% - 35px) 0, 100% 35px, 100% 100%, 0 100%)",
          }}
        />
      </Card.Header>
      <Card.Body className="pt-8" id="daily-rewards-card-body">
        <div className="flex items-start gap-8" id="daily-rewards-card-content">
          <div
            className="flex w-32 flex-col items-center gap-5"
            id="daily-rewards-card-agent-panel"
          >
            {isClaimDataLoading ? (
              <div
                className="h-24 w-24 animate-pulse rounded-sm bg-gray-800"
                id="daily-rewards-card-agent-image-skeleton"
              />
            ) : (
              <img
                alt="agent-face"
                className="h-full w-full object-cover"
                id="daily-rewards-card-agent-image"
                loading="lazy"
                src="/agent-license-face.gif"
              />
            )}
            <div className="flex w-full flex-col gap-2" id="daily-rewards-card-token-badge-wrapper">
              <span
                className="w-full rounded-sm border border-gray-700 bg-gray-800/30 px-2 py-0.5 text-center text-[10px] uppercase tracking-wider text-gray-300"
                id="daily-rewards-card-token-badge"
              >
                $TRUTH
              </span>
            </div>
          </div>
          <div className="flex-1" id="daily-rewards-card-amounts-panel">
            <div className="mt-0 flex flex-col gap-3" id="daily-rewards-card-amounts-list">
              <div
                className="flex items-baseline justify-between"
                id="daily-rewards-card-unlocked-row"
              >
                <p className="mb-0 text-xs text-gray-400" id="daily-rewards-card-unlocked-label">
                  Unlocked
                </p>
                {isClaimDataLoading ? (
                  <span
                    className="h-4 w-24 animate-pulse rounded bg-gray-800"
                    id="daily-rewards-card-unlocked-skeleton"
                  />
                ) : (
                  <h6 className="mb-0 text-gray-400" id="daily-rewards-card-unlocked-value">
                    {/* Truncated: this is claimable, so it must not read higher
                        than the claim modal will accept. */}
                    {formatClaimableAmount(calculateUnlockedRewardsResponse?.unlockedRewards)}
                  </h6>
                )}
              </div>
              <div
                className="flex items-baseline justify-between"
                id="daily-rewards-card-locked-row"
              >
                <p className="mb-0 text-xs text-gray-400" id="daily-rewards-card-locked-label">
                  Locked
                </p>
                {isClaimDataLoading ? (
                  <span
                    className="h-4 w-16 animate-pulse rounded bg-gray-800"
                    id="daily-rewards-card-locked-skeleton"
                  />
                ) : (
                  <h6 className="mb-0 text-gray-400" id="daily-rewards-card-locked-value">
                    {!!calculateUnlockedRewardsResponse &&
                      currency.convert.toFormattedDigits(
                        calculateUnlockedRewardsResponse.lockedRewards || 0,
                      )}
                  </h6>
                )}
              </div>
              <div
                className="flex items-baseline justify-between"
                id="daily-rewards-card-claimed-row"
              >
                <p className="mb-0 text-xs text-gray-400" id="daily-rewards-card-claimed-label">
                  Claimed
                </p>
                {isClaimDataLoading ? (
                  <span
                    className="h-4 w-16 animate-pulse rounded bg-gray-800"
                    id="daily-rewards-card-claimed-skeleton"
                  />
                ) : (
                  <h6 className="mb-0 text-gray-400" id="daily-rewards-card-claimed-value">
                    {!!calculateUnlockedRewardsResponse &&
                      currency.convert.toFormattedDigits(
                        calculateUnlockedRewardsResponse.totalClaimed || 0,
                      )}
                  </h6>
                )}
              </div>
              <div className="my-1 h-px w-full bg-gray-800" id="daily-rewards-card-divider" />
              <div
                className="flex items-baseline justify-between"
                id="daily-rewards-card-available-row"
              >
                <p className="text-s mb-0 text-gray-400" id="daily-rewards-card-available-label">
                  Available
                </p>
                {isClaimDataLoading ? (
                  <span
                    className="h-5 w-28 animate-pulse rounded bg-gray-800"
                    id="daily-rewards-card-available-skeleton"
                  />
                ) : (
                  <h5 className="mb-0 text-gray-300" id="daily-rewards-card-available-value">
                    {!!calculateUnlockedRewardsResponse &&
                      formatClaimableAmount(calculateUnlockedRewardsResponse.unlockedRewards)}
                    <sup className="text-xs" id="daily-rewards-card-available-ticker">
                      &nbsp;TRUTH
                    </sup>
                  </h5>
                )}
              </div>
            </div>
          </div>
        </div>

        {!!calculateUnlockedRewardsResponse?.burnPenalty &&
          calculateUnlockedRewardsResponse.burnPenalty > 0 && (
            <div
              className="mb-5 mt-4 rounded-sm border border-yellow-500/30 bg-yellow-500/5 p-3"
              id="daily-rewards-card-burn-penalty-notice"
            >
              <p
                className="mb-1 text-[10px] uppercase tracking-widest text-yellow-400"
                id="daily-rewards-card-burn-penalty-title"
              >
                Early Claim Penalty
              </p>
              <p className="mb-0 text-xs text-gray-400" id="daily-rewards-card-burn-penalty-text">
                {calculateUnlockedRewardsResponse.burnPenalty}% applies to claims until day{" "}
                {calculateUnlockedRewardsResponse.burnPenalty === 50
                  ? 31
                  : calculateUnlockedRewardsResponse.burnPenalty === 30
                    ? 93
                    : 0}
                .
                <Link
                  className="ml-1 text-xs underline"
                  href="https://gitbook.swarmnetwork.ai/agent-licenses/daily-reward-distribution/early-claim-burn-penalty"
                  id="daily-rewards-card-burn-penalty-link"
                  target="_blank"
                >
                  Learn more
                </Link>
              </p>
            </div>
          )}
      </Card.Body>

      <Card.Footer className="block overflow-visible px-0 pb-0" id="daily-rewards-card-footer">
        <div className="-mx-4" id="daily-rewards-card-claim-wrapper">
          <ClaimDailyRewards />
        </div>
      </Card.Footer>
    </Card>
  );
};
